import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import {
  Camera,
  Check,
  EllipsisVertical,
  Pencil,
  Star,
  Trash2,
  Upload,
} from "lucide-react";
import { memo, useCallback, useMemo, useState } from "react";
import { Button as PressableButton } from "react-aria-components";
import Lightbox from "yet-another-react-lightbox";
import Download from "yet-another-react-lightbox/plugins/download";

import "yet-another-react-lightbox/styles.css";
import { Button } from "@/components/base/button";
import { Checkbox } from "@/components/base/checkbox";
import {
  DropdownMenu,
  DropdownMenuItem,
} from "@/components/base/dropdown-menu";
import { Modal, ModalHeading } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { TextField } from "@/components/base/text-field";
import { addToast } from "@/components/base/toast";
import { useCanUpdateTournament } from "@/components/tournaments/context";
import type { Gender } from "@/db/schema/shared";
import { getTournamentDivisionDisplay } from "@/hooks/tournament";
import { useTRPC } from "@/integrations/trpc/react";
import { getImageUrl } from "@/supabase/storage";

import { BulkUploader } from "./bulk-uploader";
import { GalleryGrid } from "./gallery-grid";
import { buildGallerySlide } from "./utils";

const PENDING_PAGE_SIZE = 24;

type Division = {
  id: number;
  name: string | null;
  teamSize: number;
  gender: Gender;
  displayGender: boolean | null;
  displayDivision: boolean | null;
  division: {
    name: string;
    id: number;
    maxAge: number | null;
    display: string | null;
  };
};

export const GalleryPanel = memo(function GalleryPanel({
  tournamentId,
  tournamentName,
  divisions,
  isActive,
}: {
  tournamentId: number;
  tournamentName: string;
  divisions: Division[];
  isActive: boolean;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const canUpdate = useCanUpdateTournament();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [selectedDivision, setSelectedDivision] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkTags, setBulkTags] = useState<number[]>([]);
  const [pendingPage, setPendingPage] = useState(0);

  const photosQueryOptions = trpc.gallery.getPhotos.queryOptions({
    tournamentId,
  });

  const { data: photos } = useQuery({
    ...photosQueryOptions,
    enabled: isActive,
  });

  const invalidateGallery = useCallback(() => {
    queryClient.invalidateQueries(photosQueryOptions);
    queryClient.invalidateQueries(
      trpc.gallery.hasPhotos.queryOptions({ tournamentId }),
    );
    queryClient.invalidateQueries({
      queryKey: trpc.gallery.browseTournamentGalleries.queryKey(),
    });
    queryClient.invalidateQueries({
      queryKey: trpc.gallery.getPendingTournaments.queryKey(),
    });
  }, [queryClient, photosQueryOptions, trpc, tournamentId]);

  const { mutate: approvePhotos } = useMutation({
    ...trpc.gallery.approvePhotos.mutationOptions(),
    onSuccess: () => {
      invalidateGallery();
      setSelectedIds(new Set());
      setBulkTags([]);
      setPendingPage(0);
      addToast({ variant: "success", title: "Photos approved" });
    },
  });

  const { mutate: deletePhotos } = useMutation({
    ...trpc.gallery.deletePhotos.mutationOptions(),
    onSuccess: () => {
      invalidateGallery();
      setSelectedIds(new Set());
      setPendingPage(0);
      addToast({ variant: "success", title: "Photos deleted" });
    },
  });

  const { mutate: unapprovePhotos } = useMutation({
    ...trpc.gallery.unapprovePhotos.mutationOptions(),
    onSuccess: () => {
      invalidateGallery();
      addToast({ variant: "success", title: "Photo unapproved" });
    },
  });

  const { mutate: updatePhoto } = useMutation({
    ...trpc.gallery.updatePhoto.mutationOptions(),
    onSuccess: () => {
      invalidateGallery();
      addToast({ variant: "success", title: "Photo updated" });
    },
  });

  const { mutate: setCoverPhoto } = useMutation({
    ...trpc.gallery.setCoverPhoto.mutationOptions(),
    onSuccess: () => {
      invalidateGallery();
      setEditingPhoto(null);
      addToast({ variant: "success", title: "Cover photo updated" });
    },
    onError: (error) => {
      addToast({
        variant: "error",
        title: "Could not set cover",
        description: error.message,
      });
    },
  });

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  type PhotoData = NonNullable<typeof photos>[number];
  const [editingPhoto, setEditingPhoto] = useState<PhotoData | null>(null);
  const [editCaption, setEditCaption] = useState("");
  const [editPhotographer, setEditPhotographer] = useState("");
  const [editTags, setEditTags] = useState<number[]>([]);

  const openEditModal = (photo: PhotoData) => {
    setEditingPhoto(photo);
    setEditCaption(photo.caption ?? "");
    setEditPhotographer(photo.photographer ?? "");
    setEditTags(photo.divisionTags);
  };

  const saveEdit = () => {
    if (!editingPhoto) return;
    updatePhoto({
      photoId: editingPhoto.id,
      caption: editCaption || null,
      photographer: editPhotographer || null,
      divisionTags: editTags,
    });
    setEditingPhoto(null);
  };

  const { approvedPhotos, pendingPhotos } = useMemo(() => {
    if (!photos) return { approvedPhotos: [], pendingPhotos: [] };
    return {
      approvedPhotos: photos.filter((p) => p.approved),
      pendingPhotos: photos.filter((p) => !p.approved),
    };
  }, [photos]);

  const filteredApproved = useMemo(() => {
    if (!selectedDivision) return approvedPhotos;
    return approvedPhotos.filter((p) =>
      p.divisionTags.includes(selectedDivision),
    );
  }, [approvedPhotos, selectedDivision]);

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllPending = () => {
    setSelectedIds(new Set(pendingPhotos.map((p) => p.id)));
  };

  if (!photos) {
    return (
      <div className="mx-auto flex max-w-4xl flex-col gap-3 px-3 py-12">
        <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div
              key={i}
              className="aspect-square animate-pulse rounded-md bg-gray-100"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6 px-3 py-12">
      {/* Admin: Upload button */}
      {canUpdate && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {approvedPhotos.length} approved
            {pendingPhotos.length > 0 &&
              `, ${pendingPhotos.length} pending review`}
          </span>
          <Button
            variant="outline"
            size="sm"
            onPress={() => setUploadOpen(true)}
          >
            <Upload size={16} />
            Upload Photos
          </Button>
        </div>
      )}

      {/* Admin: Pending review section */}
      {canUpdate && pendingPhotos.length > 0 && (
        <div className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center justify-between">
            <h3 className={title({ size: "sm" })}>
              Pending Review ({pendingPhotos.length})
            </h3>
            <div className="flex gap-2">
              {pendingPhotos.length > PENDING_PAGE_SIZE && (
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => {
                    const visible = pendingPhotos.slice(
                      pendingPage * PENDING_PAGE_SIZE,
                      (pendingPage + 1) * PENDING_PAGE_SIZE,
                    );
                    setSelectedIds(new Set(visible.map((p) => p.id)));
                  }}
                >
                  Select Page
                </Button>
              )}
              <Button variant="outline" size="sm" onPress={selectAllPending}>
                Select All ({pendingPhotos.length})
              </Button>
              {selectedIds.size > 0 && (
                <>
                  <Button
                    variant="solid"
                    color="primary"
                    size="sm"
                    onPress={() =>
                      approvePhotos({
                        photoIds: [...selectedIds],
                        divisionTags:
                          bulkTags.length > 0 ? bulkTags : undefined,
                      })
                    }
                  >
                    <Check size={14} />
                    Approve ({selectedIds.size})
                  </Button>
                  <Button
                    variant="outline"
                    color="danger"
                    size="sm"
                    onPress={() => setConfirmDeleteOpen(true)}
                  >
                    <Trash2 size={14} />
                    Delete ({selectedIds.size})
                  </Button>
                </>
              )}
            </div>
          </div>

          {divisions.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-medium text-gray-600">
                Tag with:
              </span>
              {divisions.map((div) => (
                <Checkbox
                  key={div.id}
                  label={getTournamentDivisionDisplay(div)}
                  isSelected={bulkTags.includes(div.id)}
                  onChange={(checked) => {
                    setBulkTags((prev) =>
                      checked
                        ? [...prev, div.id]
                        : prev.filter((id) => id !== div.id),
                    );
                  }}
                />
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {pendingPhotos
              .slice(
                pendingPage * PENDING_PAGE_SIZE,
                (pendingPage + 1) * PENDING_PAGE_SIZE,
              )
              .map((photo) => (
                <PressableButton
                  key={photo.id}
                  onPress={() => toggleSelect(photo.id)}
                  className={clsx(
                    "relative aspect-square overflow-hidden rounded-md border-2 transition-colors",
                    selectedIds.has(photo.id)
                      ? "border-blue-500"
                      : "border-transparent",
                  )}
                >
                  <img
                    src={getImageUrl(
                      "tournament_gallery_photos",
                      photo.storagePath,
                      {
                        width: 200,
                        height: 200,
                        quality: 60,
                      },
                    )}
                    alt={photo.filename}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                  {selectedIds.has(photo.id) && (
                    <div className="absolute top-1 right-1 rounded-full bg-blue-500 p-0.5">
                      <Check size={12} className="text-white" />
                    </div>
                  )}
                </PressableButton>
              ))}
          </div>

          {pendingPhotos.length > PENDING_PAGE_SIZE && (
            <div className="flex items-center justify-center gap-3">
              <Button
                variant="outline"
                size="sm"
                isDisabled={pendingPage === 0}
                onPress={() => setPendingPage((p) => p - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                {pendingPage + 1} of{" "}
                {Math.ceil(pendingPhotos.length / PENDING_PAGE_SIZE)}
              </span>
              <Button
                variant="outline"
                size="sm"
                isDisabled={
                  (pendingPage + 1) * PENDING_PAGE_SIZE >= pendingPhotos.length
                }
                onPress={() => setPendingPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Division filter tags */}
      {divisions.length > 1 && approvedPhotos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <PressableButton
            onPress={() => setSelectedDivision(null)}
            className={clsx(
              "rounded-full border px-3 py-0.5 text-xs transition-colors",
              !selectedDivision
                ? "border-blue-500 bg-blue-100 text-blue-700"
                : "border-gray-200 bg-gray-100 text-gray-600 hover:border-gray-300",
            )}
          >
            All
          </PressableButton>
          {divisions.map((div) => (
            <PressableButton
              key={div.id}
              onPress={() =>
                setSelectedDivision(div.id === selectedDivision ? null : div.id)
              }
              className={clsx(
                "rounded-full border px-3 py-0.5 text-xs transition-colors",
                div.id === selectedDivision
                  ? "border-blue-500 bg-blue-100 text-blue-700"
                  : "border-gray-200 bg-gray-100 text-gray-600 hover:border-gray-300",
              )}
            >
              {getTournamentDivisionDisplay(div)}
            </PressableButton>
          ))}
        </div>
      )}

      {/* Gallery grid */}
      {approvedPhotos.length > 0 || !canUpdate ? (
        canUpdate ? (
          <>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {filteredApproved.map((photo, i) => (
                <div
                  key={photo.id}
                  className="group relative aspect-square overflow-hidden rounded-md"
                >
                  <PressableButton
                    onPress={() => setLightboxIndex(i)}
                    className="h-full w-full cursor-pointer"
                    aria-label="View photo"
                  >
                    <img
                      src={getImageUrl(
                        "tournament_gallery_photos",
                        photo.storagePath,
                        {
                          width: 400,
                          height: 400,
                          quality: 75,
                        },
                      )}
                      alt={photo.caption || photo.filename}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform group-hover:scale-105"
                    />
                  </PressableButton>
                  {photo.isCover && (
                    <div className="pointer-events-none absolute top-2 left-2 flex items-center gap-1 rounded-full bg-blue-500 px-2 py-0.5 text-xs font-medium text-white">
                      <Star size={12} />
                      Cover
                    </div>
                  )}
                  <div className="absolute top-2 right-2">
                    <DropdownMenu
                      label="Photo actions"
                      buttonVariant="icon"
                      buttonIcon={
                        <EllipsisVertical size={16} className="text-white" />
                      }
                      buttonClassName="rounded-full bg-black/50 p-1.5 hover:bg-black/70"
                    >
                      <DropdownMenuItem
                        onAction={() => openEditModal(photo)}
                        textValue="Edit"
                      >
                        <Pencil size={14} />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onAction={() => setCoverPhoto({ photoId: photo.id })}
                        isDisabled={photo.isCover}
                        textValue="Set as cover"
                      >
                        <Star size={14} />
                        {photo.isCover ? "Current cover" : "Set as cover"}
                      </DropdownMenuItem>
                    </DropdownMenu>
                  </div>
                  {(photo.caption || photo.photographer) && (
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                      {photo.caption && (
                        <p className="truncate text-xs text-white">
                          {photo.caption}
                        </p>
                      )}
                      {photo.photographer && (
                        <p className="truncate text-xs text-white/70">
                          {photo.photographer}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <Lightbox
              open={lightboxIndex >= 0}
              close={() => setLightboxIndex(-1)}
              index={lightboxIndex}
              slides={filteredApproved.map((p, i) =>
                buildGallerySlide(p, i, tournamentName),
              )}
              plugins={[Download]}
            />
          </>
        ) : (
          <GalleryGrid
            photos={filteredApproved}
            tournamentName={tournamentName}
          />
        )
      ) : (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <Camera size={32} className="text-gray-300" />
          <p className="text-sm text-gray-500">
            No approved photos yet. Upload and approve photos to show them here.
          </p>
        </div>
      )}

      {/* Upload modal */}
      <Modal isOpen={uploadOpen} onOpenChange={setUploadOpen} size="2xl">
        <div className="flex flex-col gap-4 p-6">
          <ModalHeading size="md">Upload Gallery Photos</ModalHeading>
          <p className="text-sm text-gray-500">
            Upload photos for this tournament. An admin will need to approve
            them before they appear in the public gallery.
          </p>
          <BulkUploader
            tournamentId={tournamentId}
            onComplete={() => setUploadOpen(false)}
          />
        </div>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal isOpen={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <div className="flex flex-col gap-4 p-6">
          <ModalHeading size="md">Delete Photos</ModalHeading>
          <p className="text-sm text-gray-600">
            Are you sure you want to delete {selectedIds.size} photo
            {selectedIds.size !== 1 ? "s" : ""}? This cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onPress={() => setConfirmDeleteOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="danger"
              onPress={() => {
                deletePhotos({ photoIds: [...selectedIds] });
                setConfirmDeleteOpen(false);
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* Photo edit modal */}
      {editingPhoto && (
        <Modal
          isOpen={!!editingPhoto}
          onOpenChange={(open) => {
            if (!open) setEditingPhoto(null);
          }}
          size="lg"
        >
          <div className="flex flex-col gap-4 p-6">
            <ModalHeading size="md">Edit Photo</ModalHeading>

            <img
              src={getImageUrl(
                "tournament_gallery_photos",
                editingPhoto.storagePath,
                {
                  width: 600,
                  quality: 80,
                },
              )}
              alt={editingPhoto.caption || editingPhoto.filename}
              className="w-full rounded-md object-contain"
            />

            <TextField
              label="Caption"
              value={editCaption}
              onChange={setEditCaption}
              placeholder="Add a caption..."
            />

            <TextField
              label="Photographer"
              value={editPhotographer}
              onChange={setEditPhotographer}
              placeholder="Photo credit..."
            />

            {divisions.length > 0 && (
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">Division Tags</span>
                <div className="flex flex-wrap gap-3">
                  {divisions.map((div) => (
                    <Checkbox
                      key={div.id}
                      label={getTournamentDivisionDisplay(div)}
                      isSelected={editTags.includes(div.id)}
                      onChange={(checked) => {
                        setEditTags((prev) =>
                          checked
                            ? [...prev, div.id]
                            : prev.filter((id) => id !== div.id),
                        );
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    color="danger"
                    size="sm"
                    onPress={() => {
                      unapprovePhotos({ photoIds: [editingPhoto.id] });
                      setEditingPhoto(null);
                    }}
                  >
                    Unapprove
                  </Button>
                  <Button
                    variant="outline"
                    color="danger"
                    size="sm"
                    onPress={() => {
                      deletePhotos({ photoIds: [editingPhoto.id] });
                      setEditingPhoto(null);
                    }}
                  >
                    <Trash2 size={14} />
                    Delete
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    isDisabled={editingPhoto.isCover}
                    onPress={() => setCoverPhoto({ photoId: editingPhoto.id })}
                  >
                    <Star size={14} />
                    {editingPhoto.isCover ? "Current cover" : "Set as cover"}
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onPress={() => setEditingPhoto(null)}
                  >
                    Cancel
                  </Button>
                  <Button variant="solid" onPress={saveEdit}>
                    Save
                  </Button>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                The cover photo appears on the public gallery index.
              </p>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
});
